/* Budget je Vorhaben (Paket D2, BAUPLAN-BUDGET.md, Abschnitt 6). Reine Logik ohne React: Texte, Beträge in Cent, Summen und CSV.
   Die Rechenregeln sind dieselben wie in der View finanzen_vorhaben_summen (Migration 20261015130100). */

import type { StatusOption } from "../../kern/ui/Statuswahl";
import { betragText, euro, zahlLesen, type Gelesen } from "../../kern/betrag";

export type Art = "ausgabe" | "einnahme";

export interface Posten {
  id: string;
  art: Art;
  kategorie: string;
  titel: string;
  menge: number;
  einzelpreis_cent: number;
  plan_cent: number;
  ist_cent: number | null;
  status: string;
  unterlage_id: string | null;
  notiz: string;
}

/** Unterlage, wie das Budget sie zum Verknüpfen braucht */
export interface UnterlageKurz {
  id: string;
  art: string;
  titel: string;
  /** Link oder Datei (Paket E1). Eine Datei öffnet im Budget die Vorschau. */
  quelle: "link" | "datei";
  /** null bei Dateien */
  url: string | null;
}

type Liste = readonly (readonly [string, string])[];

export const ARTEN: readonly Art[] = ["ausgabe", "einnahme"];
export const ART_MEHRZAHL: Record<Art, string> = { ausgabe: "Ausgaben", einnahme: "Einnahmen" };
export const ART_EINZAHL: Record<Art, string> = { ausgabe: "Ausgabe", einnahme: "Einnahme" };

export const KATEGORIEN: Record<Art, Liste> = {
  ausgabe: [
    ["kuenstler", "Künstler und Gagen"],
    ["technik", "Technik und Infrastruktur"],
    ["catering", "Catering und Getränke"],
    ["personal", "Personal und Sicherheit"],
    ["reise", "Reise und Unterkunft"],
    ["werbung", "Werbung"],
    ["material", "Material und Deko"],
    ["gebuehren", "Gebühren und Genehmigungen"],
    ["sonstiges", "Sonstiges"],
  ],
  einnahme: [
    ["foerderung", "Förderung"],
    ["sponsoring", "Sponsoring"],
    ["standgebuehr", "Standgebühren"],
    ["ticket", "Tickets"],
    ["spende", "Spenden"],
    ["verkauf", "Verkauf und Merch"],
    ["sonstiges", "Sonstiges"],
  ],
};

export const STATUS: Record<Art, Liste> = {
  ausgabe: [
    ["geplant", "Geplant"],
    ["angefragt", "Angefragt"],
    ["angebot", "Angebot liegt vor"],
    ["zugesagt", "Beauftragt"],
    ["bezahlt", "Bezahlt"],
  ],
  einnahme: [
    ["geplant", "Geplant"],
    ["angefragt", "Angefragt"],
    ["zugesagt", "Zugesagt"],
    ["bezahlt", "Eingegangen"],
  ],
};

/** Optionen der Statuswahl je Art (BAUPLAN-STATUSWAHL.md, Abschnitt 3), Werte und Wörter wie in STATUS */
export const STATUS_OPTIONEN: Record<Art, readonly StatusOption<string>[]> = {
  ausgabe: [
    { wert: "geplant", text: "Geplant", erklaerung: "Nur im Plan", ton: "neutral", zeichen: "gestrichelt" },
    { wert: "angefragt", text: "Angefragt", erklaerung: "Preis angefragt", ton: "warten", zeichen: "flieger" },
    { wert: "angebot", text: "Angebot liegt vor", erklaerung: "Angebot in Unterlagen", ton: "warten", zeichen: "blatt" },
    { wert: "zugesagt", text: "Beauftragt", erklaerung: "Zusage gegeben", ton: "grau", zeichen: "haken" },
    { wert: "bezahlt", text: "Bezahlt", erklaerung: "Rechnung bezahlt", ton: "ok", zeichen: "erledigt" },
  ],
  einnahme: [
    { wert: "geplant", text: "Geplant", erklaerung: "Nur im Plan", ton: "neutral", zeichen: "gestrichelt" },
    { wert: "angefragt", text: "Angefragt", erklaerung: "Antrag oder Anfrage ist raus", ton: "warten", zeichen: "flieger" },
    { wert: "zugesagt", text: "Zugesagt", erklaerung: "Zusage liegt vor", ton: "grau", zeichen: "haken" },
    { wert: "bezahlt", text: "Eingegangen", erklaerung: "Geld ist da", ton: "ok", zeichen: "erledigt" },
  ],
};

export const UNTERLAGE_ART: Record<string, string> = {
  plan: "Plan",
  angebot: "Angebot",
  vertrag: "Vertrag",
  foerderung: "Förderantrag",
  rechnung: "Rechnung",
  grafik: "Grafik",
  sonstiges: "Sonstiges",
};

const textAus = (liste: Liste, wert: string) => liste.find(([w]) => w === wert)?.[1] ?? wert;
export const kategorieText = (art: Art, kategorie: string) => textAus(KATEGORIEN[art], kategorie);
export const statusText = (art: Art, status: string) => textAus(STATUS[art], status);
export const unterlageText = (u: Pick<UnterlageKurz, "art" | "titel">) => `${UNTERLAGE_ART[u.art] ?? u.art}: ${u.titel}`;

/* Beträge --------------------------------------------------------------------------------------------------------------------- */

// Eingabe und Anzeige von Beträgen liegen im Kern (kern/betrag.ts), damit auch die Summen eines Förderantrags sie nutzen, ohne dass
// die Module einander importieren (BAUPLAN-VORHABEN-ARTEN.md, Abschnitt 5)
export { betragText, centLesen, euro, HOECHSTBETRAG_CENT, type Gelesen } from "../../kern/betrag";

/** Mit Plus vor positiven Beträgen, für Ergebnis und Differenz */
export const mitVorzeichen = (cent: number) => (cent > 0 ? "+" : "") + euro(cent);
/** „2,5“, „1“ */
export const mengeText = (menge: number) => menge.toLocaleString("de-DE", { maximumFractionDigits: 2, useGrouping: false });

/** Menge größer als 0, höchstens zwei Nachkommastellen (wie numeric(10,2)) */
export function mengeLesen(text: string): Gelesen {
  const n = zahlLesen(text);
  if (n === null || n === undefined || n <= 0) return { ok: false, fehler: "Die Menge ist eine Zahl größer als 0, zum Beispiel 2 oder 2,5." };
  if (n >= 100_000_000) return { ok: false, fehler: "Die Menge ist zu groß." };
  return { ok: true, wert: Math.round(n * 100) / 100 };
}

/** Plan wie die generated column: Menge mal Einzelpreis, gerundet */
export const planAus = (menge: number, einzelpreisCent: number) => Math.round(menge * einzelpreisCent);

/* Summen ---------------------------------------------------------------------------------------------------------------------- */

export interface Summen {
  ausgabenPlan: number;
  ausgabenIst: number;
  einnahmenPlan: number;
  einnahmenIst: number;
  /** Einnahmen minus Ausgaben, Plan */
  ergebnisPlan: number;
  /** Einnahmen minus Ausgaben, je Posten das Ist, wo eingetragen, sonst der Plan */
  ergebnisVorlaeufig: number;
  /** Einnahmen minus Ausgaben, nur eingetragenes Ist */
  ergebnisIst: number;
}

export function summen(posten: readonly Posten[]): Summen {
  const s = { ausgabenPlan: 0, ausgabenIst: 0, einnahmenPlan: 0, einnahmenIst: 0, ausgabenVorl: 0, einnahmenVorl: 0 };
  for (const p of posten) {
    const vorl = p.ist_cent ?? p.plan_cent;
    if (p.art === "ausgabe") {
      s.ausgabenPlan += p.plan_cent;
      s.ausgabenIst += p.ist_cent ?? 0;
      s.ausgabenVorl += vorl;
    } else {
      s.einnahmenPlan += p.plan_cent;
      s.einnahmenIst += p.ist_cent ?? 0;
      s.einnahmenVorl += vorl;
    }
  }
  return {
    ausgabenPlan: s.ausgabenPlan,
    ausgabenIst: s.ausgabenIst,
    einnahmenPlan: s.einnahmenPlan,
    einnahmenIst: s.einnahmenIst,
    ergebnisPlan: s.einnahmenPlan - s.ausgabenPlan,
    ergebnisVorlaeufig: s.einnahmenVorl - s.ausgabenVorl,
    ergebnisIst: s.einnahmenIst - s.ausgabenIst,
  };
}

/** Ist minus Plan, nur über Posten mit Ist. null, wenn keiner ein Ist hat. */
export function differenz(posten: readonly Posten[]): number | null {
  const mit = posten.filter((p) => p.ist_cent !== null);
  if (mit.length === 0) return null;
  return mit.reduce((n, p) => n + (p.ist_cent ?? 0) - p.plan_cent, 0);
}

export interface Gruppe {
  kategorie: string;
  label: string;
  posten: Posten[];
  plan: number;
  ist: number;
  differenz: number | null;
}

/** Posten einer Art je Kategorie, in der festen Reihenfolge der Kategorien. Leere Kategorien fehlen. */
export function gruppen(posten: readonly Posten[], art: Art): Gruppe[] {
  return KATEGORIEN[art].flatMap(([kategorie, label]) => {
    const ps = posten.filter((p) => p.art === art && p.kategorie === kategorie);
    if (ps.length === 0) return [];
    return [{ kategorie, label, posten: ps, plan: ps.reduce((n, p) => n + p.plan_cent, 0), ist: ps.reduce((n, p) => n + (p.ist_cent ?? 0), 0), differenz: differenz(ps) }];
  });
}

/* Kopf und Zeilen (BAUPLAN-FEINSCHLIFF.md, Abschnitt 6) -------------------------------------------------------------------- */

/** Eine Seite der Waage: Plan, davon fest (zugesagt oder bezahlt) und davon schon da (bezahlt, eingegangen) */
export interface Seite {
  plan: number;
  fest: number;
  da: number;
}

/** Drei Kacheln: Einnahmen, Ausgaben, Ergebnis. `sicher` heißt: feste Einnahmen minus alle geplanten Ausgaben. */
export function budgetKopf(posten: readonly Posten[]): { einnahme: Seite; ausgabe: Seite; ergebnis: number; sicher: number } {
  const leer = (): Seite => ({ plan: 0, fest: 0, da: 0 });
  const s = { einnahme: leer(), ausgabe: leer() };
  for (const p of posten) {
    const x = s[p.art];
    x.plan += p.plan_cent;
    if (p.status === "zugesagt" || p.status === "bezahlt") x.fest += p.plan_cent;
    if (p.status === "bezahlt") x.da += p.plan_cent;
  }
  return { ...s, ergebnis: s.einnahme.plan - s.ausgabe.plan, sicher: s.einnahme.fest - s.ausgabe.plan };
}

/** Anteil in Prozent für die Balken, ohne Plan 0 */
export const anteil = (teil: number, ganz: number) => (ganz > 0 ? Math.max(0, Math.min(100, (teil / ganz) * 100)) : 0);

/** Abweichung eines Postens: Ist minus Plan. Schlechter heißt bei Ausgaben mehr, bei Einnahmen weniger. Ohne Ist null. */
export function abweichung(p: Pick<Posten, "art" | "plan_cent" | "ist_cent">): { betrag: number; schlechter: boolean } | null {
  if (p.ist_cent === null) return null;
  const betrag = p.ist_cent - p.plan_cent;
  return { betrag, schlechter: p.art === "ausgabe" ? betrag > 0 : betrag < 0 };
}

/** „+15,00 €“ oder „−15,00 €“ mit echtem Minuszeichen, wie im Mockup */
export const abweichungText = (cent: number) => `${cent > 0 ? "+" : cent < 0 ? "\u2212" : ""}${euro(Math.abs(cent))}`;

/** Menge klein unter dem Titel, nur wenn sie nicht 1 ist: „2 × 45,00 €“ */
export const mengeKurz = (p: Pick<Posten, "menge" | "einzelpreis_cent">) => (p.menge === 1 ? null : `${mengeText(p.menge)} × ${euro(p.einzelpreis_cent)}`);

/** Ausgaben, über die noch jemand entscheiden muss: angefragt oder Angebot liegt vor */
export const nochZuEntscheiden = (posten: readonly Posten[]) => posten.filter((p) => p.art === "ausgabe" && (p.status === "angefragt" || p.status === "angebot"));

/** Plan und Ist je Kategorie der Ausgaben, für die Balken. Kategorien ohne Betrag fehlen. */
export function ausgabenJeKategorie(posten: readonly Posten[]): { label: string; plan: number; ist: number }[] {
  return gruppen(posten, "ausgabe")
    .map((g) => ({ label: g.label, plan: g.plan, ist: g.ist }))
    .filter((g) => g.plan > 0 || g.ist > 0);
}

/* CSV ------------------------------------------------------------------------------------------------------------------------- */

/** Feld für CSV mit Semikolon. Text, der mit = + - @ beginnt, bekommt ein Hochkomma, damit Tabellenprogramme ihn nicht als Formel lesen. */
function csvFeld(wert: string, istText: boolean): string {
  const t = istText && /^[=+\-@\t\r]/.test(wert) ? `'${wert}` : wert;
  return /[;"\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
}

export const CSV_KOPF = ["Art", "Kategorie", "Titel", "Menge", "Einzelpreis", "Plan", "Ist", "Status", "Unterlage", "Link", "Notiz"];

/** CSV ohne BOM (die setzt der Download davor): Semikolon, Beträge mit Komma ohne Tausenderpunkt, Zeilen mit CRLF für Excel */
export function csvText(posten: readonly Posten[], unterlagen: readonly UnterlageKurz[]): string {
  const nachId = new Map(unterlagen.map((u) => [u.id, u]));
  const zeilen = [...ARTEN.flatMap((art) => gruppen(posten, art).flatMap((g) => g.posten))].map((p) => {
    const u = p.unterlage_id ? nachId.get(p.unterlage_id) : undefined;
    return [
      csvFeld(ART_EINZAHL[p.art], false),
      csvFeld(kategorieText(p.art, p.kategorie), false),
      csvFeld(p.titel, true),
      mengeText(p.menge),
      betragText(p.einzelpreis_cent, false),
      betragText(p.plan_cent, false),
      p.ist_cent === null ? "" : betragText(p.ist_cent, false),
      csvFeld(statusText(p.art, p.status), false),
      u ? csvFeld(u.titel, true) : "",
      u?.url ? csvFeld(u.url, true) : "",
      csvFeld(p.notiz, true),
    ].join(";");
  });
  return [CSV_KOPF.join(";"), ...zeilen].join("\r\n") + "\r\n";
}

/** „Sternenzauber 2026“ → „sternenzauber-2026-budget.csv“ */
export function csvDateiname(name: string): string {
  const kern = name
    .toLocaleLowerCase("de-DE")
    .replace(/[^a-z0-9äöüß]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${kern || "vorhaben"}-budget.csv`;
}
